// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import AdminSidebar from '../components/AdminSidebar';
import AdminHeader from '../components/AdminHeader';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StorageIcon from '@mui/icons-material/Storage';
import MapIcon from '@mui/icons-material/Map';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import './AdminDashboard.css';

const iconColors = {
  restaurants: '#43a047', // green
  drivers: '#fbc02d', // yellow
  completed: '#1976d2', // blue
  restaurant: '#43a047',
  driver: '#fbc02d',
  orders: '#00838f',
  report: '#8e24aa',
  database: '#6d4c41',
  tracking: '#e53935',
  notifications: '#ff9800',
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    restaurants: 0,
    drivers: 0,
    completedOrders: 0,
  });

  const navigate = useNavigate();
  const { t, language } = useLanguage();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const restaurants = await getDocs(collection(db, 'restaurants'));
        const drivers = await getDocs(collection(db, 'drivers'));
        const completedOrdersSnapshot = await getDocs(
          query(collection(db, 'orders'), where('status', '==', 'completed'))
        );

        setStats({
          restaurants: restaurants.size,
          drivers: drivers.size,
          completedOrders: completedOrdersSnapshot.size,
        });
      } catch (error) {
        console.error('❌ فشل تحميل الإحصائيات:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {t('loadingData')}
        </Typography>
      </Box>
    );
  }

  const actionCards = [
    {
      icon: <RestaurantIcon sx={{ fontSize: 40, color: iconColors.restaurant, bgcolor: '#e8f5e9', borderRadius: '50%', p: 1 }} />,
      title: t('admin_restaurant_management'),
      description: t('admin_dashboard_add_edit_restaurants'),
      path: '/admin/restaurant-management'
    },
    {
      icon: <DirectionsCarIcon sx={{ fontSize: 40, color: iconColors.driver, bgcolor: '#fffde7', borderRadius: '50%', p: 1 }} />,
      title: t('admin_driver_management'),
      description: t('admin_dashboard_add_track_drivers'),
      path: '/admin/driver-management'
    },
    {
      icon: <LocalShippingIcon sx={{ fontSize: 40, color: iconColors.orders, bgcolor: '#e0f7fa', borderRadius: '50%', p: 1 }} />,
      title: t('orders'),
      description: t('admin_dashboard_view_all_orders'),
      path: '/admin/orders'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: iconColors.report, bgcolor: '#f3e5f5', borderRadius: '50%', p: 1 }} />,
      title: t('reports'),
      description: t('admin_dashboard_view_reports'),
      path: '/admin/reports'
    },
    {
      icon: <StorageIcon sx={{ fontSize: 40, color: iconColors.database, bgcolor: '#efebe9', borderRadius: '50%', p: 1 }} />,
      title: t('admin_database'),
      description: t('admin_dashboard_manage_data'),
      path: '/admin/database'
    },
    {
      icon: <MapIcon sx={{ fontSize: 40, color: iconColors.tracking, bgcolor: '#ffebee', borderRadius: '50%', p: 1 }} />,
      title: t('admin_driver_tracking'),
      description: t('admin_dashboard_view_drivers_location'),
      path: '/admin/driver-tracking'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: iconColors.report, bgcolor: '#ede7f6', borderRadius: '50%', p: 1 }} />,
      title: t('admin_driver_tracking_reports'),
      description: t('admin_dashboard_view_drivers_reports'),
      path: '/admin/driver-tracking-reports'
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 40, color: iconColors.notifications, bgcolor: '#fff3e0', borderRadius: '50%', p: 1 }} />,
      title: t('notifications'),
      description: t('admin_dashboard_send_edit_notifications'),
      path: '/notifications'
    }
  ];

  return (
    <div className="restaurant-dashboard">
      <AdminSidebar />
      <div className="restaurant-dashboard-content">
        <AdminHeader adminName={t('admin_dashboard_title')} />

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('admin_dashboard_welcome')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('admin_dashboard_desc')}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                  boxShadow: '0 4px 24px 0 rgba(67,160,71,0.10)'
                }}
              >
                <RestaurantIcon sx={{ fontSize: 48, color: iconColors.restaurants, bgcolor: '#fff', borderRadius: '50%', p: 1, boxShadow: 2 }} />
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    {t('admin_dashboard_restaurants')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.restaurants}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #fffde7 0%, #fff9c4 100%)',
                  boxShadow: '0 4px 24px 0 rgba(251,192,45,0.10)'
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 48, color: iconColors.drivers, bgcolor: '#fff', borderRadius: '50%', p: 1, boxShadow: 2 }} />
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    {t('admin_dashboard_drivers')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.drivers}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  boxShadow: '0 4px 24px 0 rgba(25,118,210,0.10)'
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: iconColors.completed, bgcolor: '#fff', borderRadius: '50%', p: 1, boxShadow: 2 }} />
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    {t('admin_dashboard_completed_orders')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.completedOrders}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {actionCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={3}
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardActionArea onClick={() => navigate(card.path)}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {card.icon}
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          {card.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default Dashboard;
